import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminLayout from './components/Layout/AdminLayout';
import DashboardRealData from './pages/DashboardRealData';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import Connections from './pages/Connections';
import Users from './pages/Users';
import BenefitsEnhanced from './pages/BenefitsEnhanced';
import Agents from './pages/Agents';
import GamesWithTokens from './pages/GamesWithTokens';
import GiveawaysEnhanced from './pages/GiveawaysEnhanced';
import TokenManagement from './pages/TokenManagement';
import RedemptionsDynamic from './pages/RedemptionsDynamic';
import RedemptionsWithChat from './pages/RedemptionsWithChat';
import Analytics from './pages/Analytics';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Support from './pages/Support';
import SystemAgents from './pages/SystemAgents';
import SubscriptionTokens from './pages/SubscriptionTokens';
import PlanManagement from './pages/PlanManagement';
import TokenAllocation from './pages/TokenAllocation';
import Login from './components/Login';
import LoadingSpinner from './components/LoadingSpinner';
import RoleBasedRouter from './components/RoleBasedRouter';

// Create a dark theme for the admin panel
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#fff',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1976d2',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

// Main App Content
const AppContent = () => {
  const { user, loading } = useAuth();

  // Add loading check
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/admin/login" 
          element={user ? <Navigate to="/admin" replace /> : <Login />} 
        />
        
        {/* Protected Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <DashboardRealData />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/super-admin"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <SuperAdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/connections"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Connections />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Users />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/benefits"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <BenefitsEnhanced />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/agents"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Agents />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/games"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <GamesWithTokens />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/giveaways"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <GiveawaysEnhanced />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tokens"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <TokenManagement />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/redemptions"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <RedemptionsDynamic />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/redemptions-chat"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <RedemptionsWithChat />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Analytics />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Reports />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Settings />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Support />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/system-agents"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <SystemAgents />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/subscription-tokens"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <SubscriptionTokens />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/plans"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <PlanManagement />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/token-allocation"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <TokenAllocation />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Role-based routing for root path */}
        <Route path="/" element={<RoleBasedRouter />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
